import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Eye, ShieldCheck, User } from "lucide-react";
import type { AuditLogListItem } from "@/types/admin";
import { formatDateTime } from "@/lib/utils";
import {
  auditActionVariant,
  formatAuditAction,
  isAdminAuditAction,
} from "@/lib/auditActions";
import {
  AUDIT_LOG_COLUMNS,
  useAuditLogColumnsStore,
  type AuditLogColumnId,
} from "@/stores/auditLogColumnsStore";

interface AuditLogTableProps {
  logs: AuditLogListItem[];
  /** Index of the first row on this page, so the ordinal column continues across pages. */
  startIndex: number;
  onViewDetails: (log: AuditLogListItem) => void;
}

/** Rendered in place of a value the row does not carry. */
const EMPTY = "—";

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the column store because a width is presentation, not preference — the
 * store holds only what gets written to localStorage. Typing it as a full `Record` over the id union
 * is the point: adding a column to `AUDIT_LOG_COLUMNS` without giving it a width is a compile error,
 * not a column that silently collapses.
 *
 * Fixed widths, not the percentages this table used to have: sized to what each cell carries — a
 * full 36-character GUID, the longest action label, a description worth reading — so the excess
 * becomes a horizontal scrollbar instead of every column being squeezed into the viewport and
 * truncated.
 */
const COLUMN_WIDTHS: Record<AuditLogColumnId, string> = {
  index: "w-16",
  // A full GUID at text-xs: the ID is what an operator copies out to trace an incident, and a
  // truncated one has to be hovered first.
  entityId: "w-80",
  entityType: "w-32",
  // Wide enough that "Payment Proof Attached" stays on one line and the rows keep an even height.
  action: "w-56",
  description: "w-[480px]",
  source: "w-24",
  user: "w-64",
  occurredAt: "w-44",
  details: "w-20",
};

export function AuditLogTable({
  logs,
  startIndex,
  onViewDetails,
}: AuditLogTableProps) {
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that selects
  // it re-renders for nothing on every unrelated store write and never re-renders when a column is
  // toggled. `hidden` is the value that actually moves.
  const hiddenColumns = useAuditLogColumnsStore((state) => state.hidden);
  const isVisible = (id: AuditLogColumnId) => !hiddenColumns.includes(id);

  return (
    <>
      {/* Mobile: one card per row. The desktop column set does not survive a phone width, so the
          same fields are stacked instead of horizontally scrolled. */}
      <div className="min-h-0 flex-1 overflow-auto rounded-md border md:hidden">
        <Table>
          <TableBody>
            {logs.map((log, index) => (
              <TableRow key={log.id}>
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-muted-foreground">
                        #{startIndex + index + 1}
                      </span>
                      <Badge variant={auditActionVariant(log.action)}>
                        {formatAuditAction(log.action)}
                      </Badge>
                      {isAdminAuditAction(log.action) && (
                        <Badge
                          variant="outline"
                          className="gap-1 border-primary/30 bg-primary/10 px-1.5 text-[10px] text-primary"
                        >
                          <ShieldCheck className="h-3 w-3" />
                          Admin
                        </Badge>
                      )}
                      {log.entityType && (
                        <Badge variant="outline" className="text-xs">
                          {log.entityType}
                        </Badge>
                      )}
                    </div>
                    {log.description && (
                      <p className="text-sm text-foreground">
                        {log.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {log.username || "System"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDateTime(log.occurredAt)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs"
                        onClick={() => onViewDetails(log)}
                      >
                        <Eye className="h-3 w-3" />
                        Details
                      </Button>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/*
        `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
        table (`<div class="relative w-full overflow-auto">`). That wrapper is as tall as the whole
        table, so while it owned the horizontal overflow its scrollbar rendered below the last row,
        off-screen, and the sticky header resolved against it and scrolled away. Switching it off
        hands both axes to this height-bounded div — the same container the tenant, resource and
        payment lists use.
      */}
      <div className="scrollbar-thin hidden min-h-0 flex-1 overflow-auto rounded-md border md:block [&>div]:overflow-visible">
        <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
          {/* Same column set and order as the client's Logs screen (LogsPage.tsx), so an operator
              reading both sees the same table. The Actions column is the one addition: only the
              admin API has a detail endpoint. Category is filterable but not a column - it is
              implied by the action and would cost width the description needs.

              Generated from AUDIT_LOG_COLUMNS rather than written out, so a hidden column cannot
              leave its <col> behind — which would shift every width after it by one. */}
          <colgroup>
            {AUDIT_LOG_COLUMNS.filter((column) => isVisible(column.id)).map(
              (column) => (
                <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
              ),
            )}
          </colgroup>
          <TableHeader className="sticky top-0 z-30 bg-card">
            <TableRow>
              {isVisible("index") && (
                <TableHead className="text-center">No.</TableHead>
              )}
              {isVisible("entityId") && <TableHead>Entity ID</TableHead>}
              {isVisible("entityType") && <TableHead>Entity Type</TableHead>}
              {isVisible("action") && <TableHead>Event / Action</TableHead>}
              {isVisible("description") && <TableHead>Description</TableHead>}
              {isVisible("source") && (
                <TableHead className="text-center">Source</TableHead>
              )}
              {isVisible("user") && <TableHead>User</TableHead>}
              {isVisible("occurredAt") && (
                <TableHead className="text-center">Occurred At</TableHead>
              )}
              {isVisible("details") && (
                <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log, index) => (
              <TableRow key={log.id}>
                {isVisible("index") && (
                  <TableCell className="text-center text-xs font-medium text-muted-foreground">
                    {startIndex + index + 1}
                  </TableCell>
                )}
                {isVisible("entityId") && (
                  <TableCell>
                    {log.entityId ? (
                      <span
                        className="block truncate font-mono text-xs font-medium text-muted-foreground select-all"
                        title={log.entityId}
                      >
                        {log.entityId}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {EMPTY}
                      </span>
                    )}
                  </TableCell>
                )}
                {isVisible("entityType") && (
                  <TableCell>
                    <span className="text-xs font-medium">
                      {log.entityType || EMPTY}
                    </span>
                  </TableCell>
                )}
                {isVisible("action") && (
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {/* Severity rides on this badge's tooltip rather than a chip of its own: the
                          client screen shows exactly one badge per row, and a third chip wrapped
                          onto a second line and made the row heights uneven. It stays available as
                          a filter and is spelled out in the detail modal. */}
                      <Badge
                        variant={auditActionVariant(log.action)}
                        title={`${log.action} (${log.severity})`}
                      >
                        {formatAuditAction(log.action)}
                      </Badge>
                    </div>
                  </TableCell>
                )}
                {isVisible("description") && (
                  <TableCell>
                    <p
                      className="truncate text-sm text-foreground"
                      title={log.description ?? ""}
                    >
                      {log.description || EMPTY}
                    </p>
                  </TableCell>
                )}
                {/* Its own column rather than a chip beside the action: the trailing segment of
                    "admin.resource.started" and "resource.started" is the same word, so the label
                    cannot carry it, and a chip in the action cell is easy to miss when scanning a
                    page for operator activity. */}
                {isVisible("source") && (
                  <TableCell className="text-center">
                    {isAdminAuditAction(log.action) ? (
                      <Badge
                        variant="outline"
                        className="gap-1 border-primary/30 bg-primary/10 text-primary"
                        title="Performed from the admin console"
                      >
                        <ShieldCheck className="h-3 w-3" />
                        Admin
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-muted-foreground"
                        title="Performed by the workspace's own user"
                      >
                        Client
                      </Badge>
                    )}
                  </TableCell>
                )}
                {isVisible("user") && (
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p
                          className="truncate text-sm"
                          title={log.username ?? ""}
                        >
                          {log.username || "System"}
                        </p>

                        {/* The client screen has no equivalent - it only ever shows one tenant.
                            Here the list spans every workspace, and "Platform" is the honest label
                            for a row with no tenant at all: an admin token carries no tenant claim,
                            so everything done from this console lands that way. */}
                        <p
                          className="truncate text-xs text-muted-foreground"
                          title={log.tenantName ?? log.tenantId ?? ""}
                        >
                          {log.tenantName || log.tenantId || "Platform"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                )}
                {isVisible("occurredAt") && (
                  <TableCell className="text-center">
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDateTime(log.occurredAt)}
                    </span>
                  </TableCell>
                )}
                {isVisible("details") && (
                  <TableCell className="sticky right-0 z-10 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View audit log details"
                      onClick={() => onViewDetails(log)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
